import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { smartDateDuration } from 'utils/date';
import ButtonGroup from 'components/ButtonGroup';
import ClassFilePicker from './ClassFilePicker/ClassFilePicker';
import Badge from 'components/Badge';
import messages from './Messages';
import {
  black,
  blue,
  gray,
  green,
  lightGray,
  red,
  yellow,
} from 'utils/style/colors';
import Icon from 'components/Icon';
import CKEditorField from 'components/CKEditorField';
// import ClassicEditor from '@rastech-inc/ckeditor';
import { CKEditorReadOnlyConfig } from 'components/CKEditorField/CKEditorField';
import { ClassViewerWrapper } from '../PDPTerms/style';
import Select from 'components/Select';
import Button from 'components/Button';
import Modal from 'components/Modal';
import {
  HintIcon,
  HintText,
  QuestionHintBox,
} from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/GeneralQuestion/styles';
import LoadingIcon from 'components/LoadingIcon';
import { LoadingCircleIndicatorNoPad } from 'components/LoadingCircleIndicator/LoadingCircleIndicator';
import {
  CompanyPDPState,
  Participant,
  ParticipantPresence,
  ParticipantStatus,
  PDPClass,
} from 'store/pdp/types';
import {
  ClassPhaseProp,
  ICompany,
  ICompanyProviders,
} from 'store/companies/companiesTypes';
import { ITags, ITagView } from 'store/providers/providerTypes';
import { IUsers } from 'store/store';
import { ISurveyItem, ISurveyResponseView } from 'store/survey/surveyTypes';
import { canIFillFn } from 'store/survey/surveySlice';
import Collapsible from '../../../../components/Collapsible';
import { getSurveyFilterTags } from './ClassPhaseEditorClass';

interface IComponentProps {
  templateClass: PDPClass;
  company: ICompany;
  tags: ITags['data'];
  relatedProviders: ICompanyProviders;
  pdpState: CompanyPDPState;
  thisUserId: number;
  users: IUsers['map'];
  isAdmin?: boolean;
  setMyGroup: (classId: number, group: string) => Promise<any>;
  canIFill: (
    response: ISurveyResponseView,
    surveyItemData: ISurveyItem
  ) => ReturnType<typeof canIFillFn>;
  pdpId: number;
}

function getClassStatusColor(status: ParticipantStatus | undefined) {
  switch (status) {
    case 'Dropped':
      return red;
    case 'Rejected':
      return red;
    case 'Approved':
      return green;
    case 'Attended':
      return yellow;
    default:
      return red;
  }
}
function getClassPresenceColor(status: ParticipantPresence | null) {
  switch (status) {
    case 'Present':
      return green;
    case 'Absent':
      return red;
    default:
      return yellow;
  }
}

function disabedPhase(
  classPhaseProp: ClassPhaseProp,
  now: number,
  groupToView: string,
  myMembership?: Participant
) {
  const phaseOverride = myMembership?.participantProps.phasePropOverride?.find(
    (it) =>
      it.phaseTagId === classPhaseProp.phaseTagId &&
      it.phaseStageId === classPhaseProp.phaseStageId
  );
  const phaseStartDate =
    phaseOverride?.startDate || classPhaseProp.phaseStartDates[groupToView];
  const phaseEndDate =
    phaseOverride?.endDate || classPhaseProp.phaseEndDates[groupToView];
  if ((classPhaseProp.adminUsers?.length || 0) > 0) return true;
  return (
    classPhaseProp.contentVisible === 'DuringPhase' &&
    (!phaseStartDate ||
      phaseStartDate! > now ||
      !phaseEndDate ||
      phaseEndDate! < now)
  );
}

const ClassViewer: React.FC<IComponentProps> = ({
  templateClass,
  company,
  tags,
  relatedProviders,
  pdpState,
  thisUserId,
  users,
  isAdmin,
  setMyGroup,
  canIFill,
  pdpId,
}) => {
  const [clazz, setClass] = useState(templateClass);
  const [savingGroup, setSavingGroup] = useState(null as string | null);
  const userRoles = new Set(
    Object.values(company?.members?.[thisUserId]?.roles || {}).map(
      (it) => `R${it.id}`
    )
  );
  useEffect(() => {
    setClass(templateClass);
  }, [templateClass]);
  const classPhases = useMemo(() => {
    const tagPhases = [] as ITagView[];
    Object.values(tags).forEach((t: ITagView) => {
      if (
        t.parentId ===
        company.pdpPanels.find((el) => el.id === pdpId)!.properties
          .classPhaseParentTag
      )
        tagPhases.push(t);
    });
    company.pdpPanels.find((el) => el.id === pdpId)!.properties
      .classPhaseParentTag;
    return tagPhases.sort(
      (a, b) => (a.order || Number(a.id)) - (b.order || Number(b.id))
    );
  }, [templateClass]);
  const now = new Date().getTime();
  const myMembership = clazz.participants[thisUserId];
  const [groupToView, setGroupToView] = useState(
    undefined as string | undefined
  );
  useEffect(() => {
    const groupToView =
      myMembership?.participantProps?.group ||
      (isAdmin
        ? clazz.properties.classGroups[0]?.key
        : clazz.properties.classGroups.length === 1
          ? clazz.properties.classGroups[0]?.key
          : undefined);
    setGroupToView(groupToView);
    setViewingPhase();
    // const phase = clazz.properties.phaseProps.find(
    //   (classPhaseProp) =>
    //     classPhaseProp.phaseStartDates[groupToView as string] <= now &&
    //     classPhaseProp.phaseEndDates[groupToView as string] >= now
    // );
    // setSelectedPhaseId({
    //   tagId: phase?.phaseTagId,
    //   stage: phase?.phaseStageId,
    // });
  }, [clazz]);
  const canChangeGroup = clazz.properties.allowChooseGroup === 'AllowChange';
  const groupOptions = (clazz.properties.classGroups || []).map((g) => ({
    value: g.key,
    label: g.title,
  }));
  const [resolvedSurveyPhases, setResolvedSurveyPhases] = useState(
    {} as { [key: string]: 'waiting' | 'answer' | 'noAnswer' }
  );
  const [reloadCounter, setReloadCounter] = useState(0);
  useEffect(() => {
    // if (isAdmin) return;
    // const surveyToCheck = {targetPhaseId: number, toke} []
    clazz.properties.phaseProps.forEach((p) => {
      if (p.disabled) return;
      // if (p.limitViewToFillSurveyForPart) {
      const targetPhase = p;
      const phaseStageId =
        targetPhase.phaseTagId + '_' + targetPhase.phaseStageId;
      if (resolvedSurveyPhases[phaseStageId]) return;
      const surveyDoc = targetPhase?.phaseDocuments?.find(
        (it) => it.type === 'survey'
      );

      resolvedSurveyPhases[phaseStageId] = surveyDoc ? 'waiting' : 'noAnswer';
      setResolvedSurveyPhases({ ...resolvedSurveyPhases });
      if (!surveyDoc) return;
      if (surveyDoc.itemToken) {
        const response: ISurveyResponseView = {} as any;
        response.tags = [targetPhase?.phaseTagId, -clazz.id];
        response.instance = thisUserId;
        response.surveyId = surveyDoc.surveyId!;
        response.surveyAnswerList = [];
        canIFill(response, { fillToken: surveyDoc.surveyToken } as any).then(
          (res) => {
            resolvedSurveyPhases[phaseStageId] = res.duplicateAnswerId
              ? 'answer'
              : 'noAnswer';
            setResolvedSurveyPhases({ ...resolvedSurveyPhases });
          },
          () => {
            resolvedSurveyPhases[phaseStageId] = 'noAnswer';
            setResolvedSurveyPhases({ ...resolvedSurveyPhases });
          }
        );
        // }
      }
    });
  }, [clazz, reloadCounter]);
  useEffect(() => {
    setResolvedSurveyPhases({});
    setReloadCounter(reloadCounter + 1);
  }, [clazz]);
  const isInGroupChangePeriod =
    (clazz.properties.groupChooseStartDate ?? new Date().getTime()) <=
      new Date().getTime() &&
    (clazz.properties.groupChooseEndDate ?? new Date().getTime()) >=
      new Date().getTime();

  const [groupSelectModalOpen, setGroupSelectModalOpen] = useState(false);

  const multiStageTagIds = new Set(
    company.pdpPanels
      .find((el) => el.id === pdpId)!
      .properties.classPhaseDefaultProps?.filter(
        (it) => it.limitStateToOne !== true
      )
      .map((it) => it.phaseTagId)
  );
  const stages = new Set( // stage = -1 for non recurring stages
    clazz.properties.phaseProps
      .filter((s) => {
        return !s.disabled;
      })
      .map((it) =>
        multiStageTagIds.has(it.phaseTagId) ? it.phaseStageId! : -1
      )
  );
  classPhases.filter((it: ITagView) => !multiStageTagIds.has(+it.id));
  const [selectedPhaseId, setSelectedPhaseId] = useState({
    tagId: 0 as number | undefined,
    stage: 0 as number | undefined,
  });
  const selectedPhase = clazz.properties.phaseProps.find(
    (it) =>
      it.phaseTagId === selectedPhaseId.tagId &&
      it.phaseStageId === selectedPhaseId.stage
  ) as ClassPhaseProp;
  const selectedPhaseLimitFillTagStageId =
    selectedPhase?.limitViewToFillSurveyForPart +
    '_' +
    selectedPhase?.phaseStageId;
  let lockState: 'false' | 'true' | 'waiting' = 'false';
  let lockMessage = '';
  if (
    selectedPhase &&
    !isAdmin &&
    selectedPhase.limitViewToPresentMembers &&
    myMembership.participantProps.present !== 'Present'
  ) {
    lockState = 'true';
    lockMessage = messages.phaseLimitToPresntMember;
  }
  if (
    lockState === 'false' &&
    selectedPhase &&
    !isAdmin &&
    selectedPhase.limitViewToFillSurveyForPart
  ) {
    if (
      (resolvedSurveyPhases[selectedPhaseLimitFillTagStageId] ?? 'waiting') ===
      'waiting'
    ) {
      lockState = 'waiting';
      lockMessage = messages.loading;
    } else if (
      resolvedSurveyPhases[selectedPhaseLimitFillTagStageId] === 'answer'
    )
      lockState = 'false';
    else {
      lockState = 'true';
      lockMessage = messages.notAccesibleDuToIncompletePhase(
        tags[selectedPhase.limitViewToFillSurveyForPart]?.text
      );
    }
  }

  function setViewingPhase() {
    const now = new Date().getTime();
    if (!groupToView) {
      setSelectedPhaseId({ stage: undefined, tagId: undefined });
    } else if (
      selectedPhase &&
      disabedPhase(selectedPhase, now, groupToView as string)
    ) {
      setSelectedPhaseId({ stage: undefined, tagId: undefined });
    } else if (!selectedPhaseId?.tagId) {
      const newSelectedPhase = clazz.properties.phaseProps.find((it) => {
        if (it.disabled) return false;
        const phaseOverride =
          myMembership?.participantProps?.phasePropOverride?.find(
            (p) =>
              p.phaseTagId === it.phaseTagId &&
              p.phaseStageId === it.phaseStageId
          );
        const startDate =
          phaseOverride?.startDate || it.phaseStartDates[groupToView as string];
        const endDate =
          phaseOverride?.endDate || it.phaseEndDates[groupToView as string];
        return (
          startDate <= now &&
          endDate >= now &&
          !disabedPhase(it, now, groupToView as string)
        );
      });

      setSelectedPhaseId({
        stage: newSelectedPhase?.phaseStageId,
        tagId: newSelectedPhase?.phaseTagId,
      });
    }
  }

  function addDateToUpdate(dateToCheck: number | undefined) {
    const now = new Date().getTime();
    if (dateToCheck && dateToCheck > now)
      setTimeout(
        () => {
          setReloadCounter(reloadCounter + 1);
        },
        dateToCheck - now + 1000
      );
  }
  useEffect(() => {
    setViewingPhase();
    if (groupToView)
      clazz.properties.phaseProps.forEach((p) => {
        addDateToUpdate(p.phaseStartDates[groupToView as string]);
        addDateToUpdate(p.phaseEndDates[groupToView as string]);
      });
  }, [clazz, groupToView]);
  const groupToViewObj = clazz.properties.classGroups.find(
    (it) => it.key === groupToView
  );
  const topic = pdpState.topics?.[clazz.topicId];

  const selectedGroup = clazz.properties.classGroups.find(
    (item) => item.key === groupToView
  );

  userRoles.add(`U${thisUserId}`);

  let classFromDate: Date | undefined;
  let classToDate: Date | undefined;
  classFromDate = clazz.startDate ? new Date(clazz.startDate) : undefined;
  classToDate = clazz.endDate ? new Date(clazz.endDate) : undefined;
  if (!isAdmin) {
    const userGroup = myMembership?.participantProps?.group;
    if (userGroup) {
      let minStartDate: number | undefined;
      let maxEndDate: number | undefined;

      clazz.properties.phaseProps.forEach((phase) => {
        if (!phase.disabled) {
          const phaseStartDate = phase.phaseStartDates[userGroup];
          const phaseEndDate = phase.phaseEndDates[userGroup];

          // Check participant props override
          const participantPhaseProps =
            myMembership?.participantProps?.phasePropOverride?.find(
              (p) =>
                p.phaseTagId === phase.phaseTagId &&
                p.phaseStageId === phase.phaseStageId
            );

          const effectiveStartDate =
            participantPhaseProps?.startDate || phaseStartDate;
          const effectiveEndDate =
            participantPhaseProps?.endDate || phaseEndDate;

          if (effectiveStartDate) {
            minStartDate = minStartDate
              ? Math.min(minStartDate, effectiveStartDate)
              : effectiveStartDate;
          }
          if (effectiveEndDate) {
            maxEndDate = maxEndDate
              ? Math.max(maxEndDate, effectiveEndDate)
              : effectiveEndDate;
          }
        }
      });

      classFromDate = minStartDate ? new Date(minStartDate) : classFromDate;
      classToDate = maxEndDate ? new Date(maxEndDate) : classToDate;
    }
  }

  return (
    <ClassViewerWrapper>
      {clazz && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px',
              borderBottom: `1px solid ${lightGray}`,
              marginBottom: '10px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span
                style={{ display: 'flex', gap: '2px', alignItems: 'center' }}
              >
                <h5>{topic?.properties.name}</h5>
                {clazz.properties.classGroups.length > 0 && (
                  <>
                    :
                    {isAdmin ? (
                      <Select
                        multiChoice={false}
                        allowRemove={false}
                        round={true}
                        placeholder={messages.group}
                        onChangeValues={(v) => {
                          setGroupToView(v[0].value.toString());
                        }}
                        options={groupOptions}
                        values={groupOptions.filter(
                          (it) => it.value === groupToView
                        )}
                      />
                    ) : (
                      <span>
                        {groupToViewObj?.title ?? messages.unknownGroup}
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>

            {clazz.properties.holdType && (
              <Badge color={gray}>
                <Icon style={{ marginLeft: '3px' }} type="bookmark" />
                {messages.holdType} :{' '}
                {
                  tags[groupToViewObj?.holdType ?? clazz.properties.holdType]
                    ?.text
                }
              </Badge>
            )}
            {clazz.properties.location && (
              <Badge color={black}>
                <Icon style={{ marginLeft: '3px' }} type="map-pin" />
                {messages.location} :{' '}
                {
                  tags[groupToViewObj?.location ?? clazz.properties.location]
                    ?.text
                }
              </Badge>
            )}
            <Badge color="blue">
              <Icon style={{ marginLeft: '3px' }} type={'calendar'} />
              {smartDateDuration(classFromDate, classToDate, true)}
            </Badge>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {!!clazz.managerUserId && (
              <Badge color="blue">
                <Icon style={{ marginLeft: '3px' }} type="user" />
                {messages.classAdmin}
                {' : '}
                {users[clazz.managerUserId]?.name}
              </Badge>
            )}
            {
              <Badge color="green">
                <>
                  <Icon style={{ marginLeft: '3px' }} type="user" />
                  {messages.teacherName} :{' '}
                  {selectedGroup && selectedGroup.teacherUserId
                    ? users[selectedGroup.teacherUserId]?.name
                    : users[clazz.teacherUserId || 0]?.name ||
                      clazz.properties.teacher ||
                      '--'}
                </>
              </Badge>
            }

            <Badge
              color={getClassStatusColor(
                myMembership?.participantProps?.status
              )}
              textColor={black}
            >
              {messages.myStatus}
              {' : '}
              {
                messages[
                  myMembership?.participantProps?.status || 'NotAttended'
                ]
              }
            </Badge>
            <Badge
              color={getClassPresenceColor(
                myMembership?.participantProps?.present
              )}
              textColor={black}
            >
              {messages.presence}
              {' : '}
              {messages[myMembership?.participantProps?.present || 'unknown']}
            </Badge>
          </div>
        </>
      )}

      {clazz.properties.description &&
        clazz.properties.description.trim() !== '' && (
          <div style={{ marginTop: '10px' }}>
            <CKEditorField
              // editor={ClassicEditor}
              data={clazz.properties.description?.replace(/\n/g, '<br>') || ''}
              disabled={true}
              dir="rtl"
              config={CKEditorReadOnlyConfig}
            />
          </div>
        )}
      {!isAdmin && !groupToView && !canChangeGroup && (
        <p>{messages.notAddedToAnyGroup}</p>
      )}
      {!isAdmin && !groupToView && canChangeGroup && !isInGroupChangePeriod && (
        <p>
          {messages.pleaseReturnInSelectedPeriod(
            smartDateDuration(
              clazz.properties.groupChooseStartDate,
              clazz.properties.groupChooseEndDate
            )
          )}
        </p>
      )}
      {!isAdmin && !groupToView && canChangeGroup && isInGroupChangePeriod && (
        <>
          <p>
            {messages.noGroupNotice(
              smartDateDuration(
                clazz.properties.groupChooseStartDate,
                clazz.properties.groupChooseEndDate,
                true
              )
            )}
          </p>
        </>
      )}
      {!isAdmin && canChangeGroup && isInGroupChangePeriod && (
        <>
          <Button
            color={groupToView ? 'black-outline' : 'green'}
            onClick={() => setGroupSelectModalOpen(true)}
            style={{ margin: '1rem' }}
          >
            {groupToView ? messages.changeGroup : messages.setGroup}
          </Button>
        </>
      )}
      <Modal
        title={messages.selectAGroup}
        isActive={groupSelectModalOpen}
        onClose={() => setGroupSelectModalOpen(false)}
      >
        {clazz.properties.groupChooseDescription && (
          <CKEditorField
            // editor={ClassicEditor}
            data={clazz.properties.groupChooseDescription}
            disabled={true}
            dir="rtl"
            config={CKEditorReadOnlyConfig}
          />
        )}
        <table>
          <thead>
            <th>{messages.group}</th>
            {[...clazz.properties.phaseProps]
              .sort((a, b) => {
                return (
                  (tags[a.phaseTagId]?.order || 0) -
                  (tags[b.phaseTagId]?.order || 0)
                );
              })
              .map((phaseProp) =>
                !phaseProp.disabled ? (
                  <th key={phaseProp.phaseTagId}>
                    {tags[phaseProp.phaseTagId]?.text}
                  </th>
                ) : (
                  <></>
                )
              )}
            <th>{}</th>
          </thead>
          <tbody>
            {clazz.properties.classGroups.map((classGroup) => (
              <tr key={classGroup.key}>
                <td>{classGroup.title}</td>
                {clazz.properties.phaseProps.map((phaseProp) =>
                  !phaseProp.disabled ? (
                    <th key={phaseProp.phaseTagId}>
                      {smartDateDuration(
                        phaseProp.phaseStartDates[classGroup.key],
                        phaseProp.phaseEndDates[classGroup.key],
                        true
                      )}
                    </th>
                  ) : (
                    <></>
                  )
                )}
                <td>
                  <Button
                    color={'green'}
                    disabled={!!savingGroup}
                    loading={savingGroup === classGroup.key}
                    onClick={() => {
                      setSavingGroup(classGroup.key);
                      setMyGroup(clazz.id, classGroup.key).then(() => {
                        setSavingGroup(null);
                        setGroupSelectModalOpen(false);
                        setGroupToView(classGroup.key);
                      });
                    }}
                  >
                    {messages.select}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
      {groupToView && (
        <>
          <p style={{ fontSize: '13px' }}>
            {messages.classPhases}:{' '}
            {clazz.properties.classGroups.length > 1 && !isAdmin && (
              <Badge>
                {
                  clazz.properties.classGroups.find(
                    (it) => it.key === groupToView
                  )?.title
                }
              </Badge>
            )}
          </p>
          {groupToView && stages.size > 0 ? (
            [...stages].map((stage) => {
              let foundSelected = false;
              const buttons = classPhases.reduce(
                (current, item) => {
                  const classPhaseProp = clazz.properties.phaseProps.find(
                    (it) =>
                      it.phaseTagId === item.id &&
                      !it.disabled &&
                      (multiStageTagIds.has(it.phaseTagId)
                        ? stage === it.phaseStageId
                        : stage === -1)
                  );
                  if (classPhaseProp) {
                    const disabledForMember = disabedPhase(
                      classPhaseProp,
                      now,
                      groupToView as string,
                      isAdmin ? undefined : myMembership
                    );
                    const isAdminInPhase = classPhaseProp.adminUsers?.some(
                      (it) => userRoles.has(it)
                    );
                    // if (!(isAdminInPhase || isAdmin) && classPhaseProp.disabled)
                    //   return current;

                    if (
                      selectedPhaseId.stage === classPhaseProp.phaseStageId &&
                      selectedPhaseId.tagId === classPhaseProp.phaseTagId
                    )
                      foundSelected = true;
                    const itemTagStageId =
                      item.id + '_' + classPhaseProp.phaseStageId;
                    const buttonId = item.order + '_' + itemTagStageId;
                    const phaseOverwrite =
                      myMembership?.participantProps?.phasePropOverride?.find(
                        (p) =>
                          p.phaseTagId === classPhaseProp.phaseTagId &&
                          p.phaseStageId === classPhaseProp.phaseStageId
                      );
                    const disabled =
                      isAdmin || isAdminInPhase ? false : disabledForMember;
                    current[buttonId] = {
                      disabled: disabled,
                      value: (
                        <span
                          key={item.id}
                          aria-disabled={disabled}
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          {resolvedSurveyPhases[itemTagStageId] && (
                            <>
                              {resolvedSurveyPhases[itemTagStageId] ===
                              'waiting' ? (
                                <LoadingCircleIndicatorNoPad
                                  style={{
                                    margin: 0,
                                    padding: 0,
                                  }}
                                  innerStyle={{
                                    width: '20px',
                                    height: '20px',
                                  }}
                                />
                              ) : resolvedSurveyPhases[itemTagStageId] ===
                                'answer' ? (
                                <Icon
                                  type={'check'}
                                  color={blue}
                                  style={{ fontSize: '18px' }}
                                />
                              ) : (
                                <span />
                              )}
                            </>
                          )}
                          <div style={{ flexGrow: 1 }}>
                            {item.text}
                            <br />
                            {smartDateDuration(
                              phaseOverwrite?.startDate ||
                                classPhaseProp.phaseStartDates[
                                  groupToView as string
                                ],
                              phaseOverwrite?.endDate ||
                                classPhaseProp.phaseEndDates[
                                  groupToView as string
                                ],
                              true
                            )}
                          </div>
                        </span>
                      ),
                    };
                  }
                  return current;
                },
                {} as {
                  [key: string]: { disabled: boolean; value: ReactElement };
                }
              );
              const buttonGroup = (
                <>
                  <ButtonGroup
                    items={buttons}
                    width={'90%'}
                    style={{
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      position: 'relative',
                      // marginTop: '-60px',
                      zIndex: '10',
                      // transform: 'translateY(60px)',
                    }}
                    onSelect={(item) => {
                      const strings = item.split('_');
                      setSelectedPhaseId({
                        tagId: Number(strings[1]),
                        stage: Number([strings[2]]),
                      });
                    }}
                    selected={
                      tags[Number(selectedPhaseId.tagId)]?.order +
                      '_' +
                      selectedPhaseId.tagId +
                      '_' +
                      selectedPhaseId.stage
                    }
                  />
                </>
              );
              return (
                <Collapsible
                  isOpen={foundSelected}
                  key={stage}
                  nested={true}
                  customHeaderStyle={{ flex: 1 }}
                  hideChevron={true}
                  header={
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '1rem',
                      }}
                    >
                      <span style={{ flexShrink: 0 }}>
                        {stage === -1
                          ? messages.otherStage
                          : messages.stageNumber(stage + 1)}
                      </span>
                      {buttonGroup}
                    </div>
                  }
                >
                  {selectedPhase &&
                    (lockState === 'false' ? (
                      <ClassFilePicker
                        editable={false}
                        items={selectedPhase.phaseDocuments}
                        onRemove={() => {
                          /*do nothing*/
                        }}
                        onSave={() => {
                          /*do nothing*/
                        }}
                        providers={relatedProviders}
                        surveyFilterTags={getSurveyFilterTags(
                          clazz,
                          selectedPhase
                        )}
                        showCorrectAnswers={selectedPhase.showCorrectAnswers}
                        phaseEndDate={
                          myMembership?.participantProps?.phasePropOverride?.find(
                            (p) =>
                              p.phaseTagId === selectedPhase.phaseTagId &&
                              p.phaseStageId === selectedPhase.phaseStageId
                          )?.endDate ||
                          selectedPhase.phaseEndDates[groupToView as string]
                        }
                        clazz={clazz}
                        topic={topic}
                        onCloseSurvey={() => {
                          setReloadCounter(reloadCounter + 1);
                        }}
                      />
                    ) : (
                      <div style={{ margin: '20px auto' }}>
                        {lockState === 'true' && (
                          <Icon
                            type={'lock'}
                            color={red}
                            style={{ margin: '10px' }}
                          />
                        )}
                        {lockState === 'waiting' && (
                          <span style={{ margin: '10px' }}>
                            <LoadingIcon color={'blue'} />
                          </span>
                        )}
                        <span>{lockMessage}</span>
                      </div>
                    ))}
                </Collapsible>
              );
            })
          ) : (
            <QuestionHintBox color={red}>
              <HintIcon type="alert-circle" color={red} />
              <HintText style={{ color: red }}>
                <span>{messages.noActivePhaseFound}</span>
              </HintText>
            </QuestionHintBox>
          )}
        </>
      )}
    </ClassViewerWrapper>
  );
};

export default ClassViewer;
